import { useUpdateInvestigation } from "#src/components/hooks/adapters/useInvestigations";
import {
  Button,
  Dialog,
  Form,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  MAX_INVESTIGATION_TITLE_LENGTH,
  type InvestigationType,
} from "@validereinc/domain";
import React, { useCallback, useEffect } from "react";

type RenameInvestigationTitleFormType = {
  title: string;
};

const RENAME_INVESTIGATION_TITLE_DEFAULT_VALUES: RenameInvestigationTitleFormType =
  {
    title: "",
  };

type RenameInvestigationTitleDialogProps = {
  investigation: InvestigationType;
  isOpen: boolean;
  onClose: () => void;
};

export const RenameInvestigationTitleDialog = ({
  investigation,
  isOpen,
  onClose,
}: RenameInvestigationTitleDialogProps) => {
  const form = useForm<RenameInvestigationTitleFormType>({
    defaultValues: RENAME_INVESTIGATION_TITLE_DEFAULT_VALUES,
  });

  const updateInvestigationMutation = useUpdateInvestigation({
    onSuccess: () => {
      onClose();
    },
  });

  const isFormLoading = updateInvestigationMutation.isLoading;

  const handleClose = useCallback(() => {
    if (isFormLoading) {
      return;
    }

    form.reset(RENAME_INVESTIGATION_TITLE_DEFAULT_VALUES);
    onClose();
  }, [form, isFormLoading, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    form.reset({
      title: investigation.title?.trim() ?? "",
    });
  }, [isOpen, investigation.id]);

  const submitHandler = form.handleSubmit(({ title }) => {
    updateInvestigationMutation.mutate({
      id: investigation.id,
      data: { title: title.trim() },
    });
  });

  return (
    <Form
      {...form}
      onSubmit={submitHandler as () => void}
    >
      <Dialog
        isOpen={isOpen}
        onClose={handleClose}
        title="Rename Investigation"
        actionRow={[
          <Button
            key="rename-investigation-action"
            variant="primary"
            onClick={submitHandler}
            type="submit"
            isLoading={isFormLoading}
            disabled={isFormLoading || !form.formState.isValid}
          >
            Save
          </Button>,
        ]}
      >
        <TextInput
          label="Title"
          name="title"
          placeholder="Enter a title..."
          type="text"
          isFluid
          isRequired
          isDisabled={isFormLoading}
          rules={{
            validate: {
              isNonEmpty: (value) =>
                Boolean(value?.trim()) || "Title is required.",
              maxLength: (value) =>
                (value?.trim().length ?? 0) <= MAX_INVESTIGATION_TITLE_LENGTH ||
                `Title must be ${MAX_INVESTIGATION_TITLE_LENGTH} characters or fewer.`,
            },
          }}
        />
      </Dialog>
    </Form>
  );
};
